import logger from 'logger'

const log = logger.extend('appsync-ws')

const {
  VITE_GQL_ENDPOINT: httpEndpoint,
  VITE_GQL_REALTIME_ENDPOINT: explicitRealtimeEndpoint
} = import.meta.env

const getRealtimeUrl = (): string => {
  if (typeof explicitRealtimeEndpoint === 'string' && explicitRealtimeEndpoint !== '') return explicitRealtimeEndpoint
  return (httpEndpoint as string)
    .replace(/^https:\/\//, 'wss://')
    .replace(/appsync-api/, 'appsync-realtime-api')
}

const getAppSyncHost = (): string => {
  const realtimeUrl = new URL(getRealtimeUrl())
  return realtimeUrl.host.replace('-realtime-api', '-api')
}

const AUTH_ERROR_CODES = new Set([401, 403, 4401, 4403])

const isAuthError = (err: any): boolean => {
  const code = err?.code ?? err?.response?.status ?? err?.statusCode
  if (typeof code === 'number' && AUTH_ERROR_CODES.has(code)) return true
  const msg = String(err?.message ?? err ?? '').toLowerCase()
  return msg.includes('unauthorized') || msg.includes('forbidden')
}

const makeId = (): string => {
  try {
    const uuid = globalThis.crypto?.randomUUID?.()
    if (uuid !== undefined) return uuid
  } catch { }
  const bytes = new Uint8Array(16)
  globalThis.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

const randomFloat = (): number => {
  const values = new Uint32Array(1)
  globalThis.crypto.getRandomValues(values)
  return values[0] / 2 ** 32
}

const b64 = (obj: any): string =>
  encodeURIComponent(btoa(JSON.stringify(obj)))

export interface SubscribePayload {
  query: string
  variables?: Record<string, any>
}

export interface SubscribeCallbacks {
  next: (data: any) => void
  error: (err: Error) => void
  complete: () => void
}

export interface AppSyncClient {
  subscribe: (payload: SubscribePayload, callbacks: SubscribeCallbacks) => () => void
  on: (event: 'connected' | 'closed', cb: () => void) => () => void
  dispose: () => void
}

export const WEBSOCKET_SUB_QUERY = `
  subscription WebsocketSub($channel: String!) {
    WebsocketSub(channel: $channel) {
      channel
      data
    }
  }
`

interface ActiveSubscription {
  payload: SubscribePayload
  callbacks: SubscribeCallbacks
}

interface ClientOptions {
  retryAttempts?: number
  shouldRetry?: (err: any) => boolean
}

class AppSyncWsClient implements AppSyncClient {
  private ws: WebSocket | null = null
  private state: 'CLOSED' | 'CONNECTING' | 'OPEN' = 'CLOSED'
  private readonly subs = new Map<string, ActiveSubscription>()
  private connectPromise: Promise<void> | null = null
  private retryCount = 0
  private listeners: Record<'connected' | 'closed', Array<() => void>> = {
    connected: [], closed: []
  }

  private disposed = false

  private readonly maxRetries: number
  private readonly shouldRetryFn: (err: any) => boolean

  constructor (
    private readonly token: string,
    private readonly realtimeUrl: string,
    private readonly apiHost: string,
    options: ClientOptions = {}
  ) {
    this.maxRetries = options.retryAttempts ?? 30
    this.shouldRetryFn = options.shouldRetry ?? (() => true)
  }

  on (event: 'connected' | 'closed', cb: () => void): () => void {
    this.listeners[event].push(cb)
    return () => {
      this.listeners[event] = this.listeners[event].filter((f) => f !== cb)
    }
  }

  subscribe (payload: SubscribePayload, callbacks: SubscribeCallbacks): () => void {
    if (this.disposed) {
      callbacks.error(new Error('Client is disposed'))
      return () => {}
    }

    const id = makeId()
    this.subs.set(id, { payload, callbacks })

    if (this.state === 'OPEN') {
      this.sendStart(id, payload)
    } else {
      this.ensureConnected().catch((err) => {
        if (this.subs.delete(id)) {
          callbacks.error(err instanceof Error ? err : new Error(String(err)))
        }
      })
    }

    return () => {
      if (this.subs.delete(id) && this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ id, type: 'stop' }))
        } catch { }
      }
    }
  }

  dispose (): void {
    this.disposed = true
    this.subs.clear()
    try { this.ws?.close() } catch {}
    this.ws = null
  }

  private async ensureConnected (): Promise<void> {
    if (this.state === 'OPEN') return await Promise.resolve()
    if (this.connectPromise != null) return await this.connectPromise
    this.connectPromise = this.connect()
    this.connectPromise.finally(() => { this.connectPromise = null })
    return await this.connectPromise
  }

  private async connect (): Promise<void> {
    return await new Promise<void>((resolve, reject) => {
      this.state = 'CONNECTING'

      const headerObj = { host: this.apiHost, Authorization: this.token }
      const url = `${this.realtimeUrl}?header=${b64(headerObj)}&payload=${b64({})}`

      const ws = new WebSocket(url, ['graphql-ws'])
      this.ws = ws

      const ackTimeout = setTimeout(() => {
        try { ws.close() } catch {}
        reject(new Error('AppSync connection_ack timed out'))
      }, 10_000)

      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ type: 'connection_init' }))
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)))
        }
      }

      ws.onmessage = (event) => {
        let msg: any
        try {
          msg = JSON.parse(event.data)
        } catch (err) {
          log({ onmessage: 'parse failed', err: err instanceof Error ? err.name : 'unknown' })
          return
        }
        this.handleMessage(msg, { ackTimeout, resolve })
      }

      ws.onerror = (event) => {
        clearTimeout(ackTimeout)
      }

      ws.onclose = (event) => {
        clearTimeout(ackTimeout)
        const wasOpen = this.state === 'OPEN'
        this.state = 'CLOSED'
        this.ws = null
        this.listeners.closed.forEach((fn) => fn())

        if (!wasOpen) {
          reject(new Error(`WebSocket closed before connection_ack (code: ${event.code})`))
        }

        if (this.disposed) return

        const shouldRetry = this.shouldRetryFn({ code: event.code })
        if (!shouldRetry || this.retryCount >= this.maxRetries) return

        this.retryCount += 1
        const delay = Math.min(1000 * 2 ** Math.min(this.retryCount, 6), 30_000) + randomFloat() * 1000
        setTimeout(() => {
          if (this.disposed) return
          this.connectPromise = this.connect()
            .then(() => {})
            .catch(() => {})
          this.connectPromise.finally(() => { this.connectPromise = null })
        }, delay)
      }
    })
  }

  private handleMessage (
    msg: any,
    ctx: { ackTimeout: NodeJS.Timeout | number, resolve: () => void }
  ): void {
    const { type } = msg

    if (type === 'connection_ack') {
      clearTimeout(ctx.ackTimeout as any)
      this.state = 'OPEN'
      this.retryCount = 0
      this.listeners.connected.forEach((fn) => fn())
      for (const [id, sub] of this.subs) {
        this.sendStart(id, sub.payload)
      }
      ctx.resolve()
      return
    }

    if (type === 'ka') {
      return
    }

    if (type === 'connection_error') {
      const errMsg = msg.payload?.errors?.[0]?.message ?? 'AppSync connection_error'
      const err = new Error(errMsg)
      ;(err as any).code = msg.payload?.errors?.[0]?.errorCode
      for (const sub of this.subs.values()) sub.callbacks.error(err)
      try { this.ws?.close() } catch { }
      return
    }

    const id: string | undefined = msg.id
    if (id == null || id === '') return
    const sub = this.subs.get(id)
    if (sub == null) return

    if (type === 'data') {
      sub.callbacks.next(msg.payload?.data)
      return
    }

    if (type === 'start_ack') {
      return
    }

    if (type === 'error') {
      const errMsg = msg.payload?.errors?.[0]?.message ?? 'AppSync subscription error'
      const err = new Error(errMsg)
      ;(err as any).code = msg.payload?.errors?.[0]?.errorCode
      sub.callbacks.error(err)
      return
    }

    if (type === 'complete') {
      sub.callbacks.complete()
      this.subs.delete(id)
    }
  }

  private sendStart (id: string, payload: SubscribePayload): void {
    if ((this.ws == null) || this.ws.readyState !== WebSocket.OPEN) return
    const startMsg = {
      id,
      type: 'start',
      payload: {
        data: JSON.stringify({ query: payload.query, variables: payload.variables ?? {} }),
        extensions: {
          authorization: {
            host: this.apiHost,
            Authorization: this.token
          }
        }
      }
    }
    try {
      this.ws.send(JSON.stringify(startMsg))
    } catch (err) {
      const sub = this.subs.get(id)
      if (sub != null) sub.callbacks.error(err instanceof Error ? err : new Error(String(err)))
    }
  }
}

export const buildAppSyncWsClient = (token: string): AppSyncClient => {
  return new AppSyncWsClient(
    token,
    getRealtimeUrl(),
    getAppSyncHost(),
    {
      retryAttempts: 30,
      shouldRetry: (err: any) => {
        if (isAuthError(err)) return false
        return true
      }
    }
  )
}
